import { Buttons } from '../../../buttons';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, model, resource, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ItemClickEvent } from 'devextreme/ui/tree_view';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { DistributionObjectService } from 'src/app/distribution/services';
import { DocumentTypeEntity } from 'src/app/distribution/models';
import { ModalDialogComponent } from '../../advanced/modal-dialog/modal-dialog.component';
import { Textbox } from '../../../textbox';
import { DateTimeService } from 'src/app/shared/datetime/datetime.service';
import { TreeViewComponent } from '../../basic/tree-view/tree-view.component';
import { SingleFileUploadComponent } from '../../basic/single-file-upload/single-file-upload.component';

enum NodeType {
  ELEMENT_NODE = 1,
  ATTRIBUTE_NODE = 2,
  TEXT_NODE = 3,
  CDATA_SECTION_NODE = 4,
  PROCESSING_INSTRUCTION_NODE = 7,
  COMMENT_NODE = 8,
  DOCUMENT_NODE = 9,
  DOCUMENT_TYPE_NODE = 10,
  DOCUMENT_FRAGMENT_NODE = 11,
}

interface MyTreeViewNode {
  id: number;
  parentId: number;
  parent: MyTreeViewNode;
  expanded: boolean;
  name: string;
  value: string | null;
  nodeType: NodeType;
  hasItems: boolean;
  itemCount: number;
}

type IdCounter = { value: number };
type BuildNodePlainStructure = (node: Element, parent: MyTreeViewNode | null, idCounter: IdCounter) => MyTreeViewNode[];

export interface SampleDocument {
  fileName: string;
  fileSize: number;
  timeStamp: Date;
  xml: string;
};

@Component({
  imports: [Buttons, ModalDialogComponent, SingleFileUploadComponent, Textbox, TreeViewComponent],
  templateUrl: './xpath-helper-dialog.component.html',
  styleUrl: './xpath-helper-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XPathHelperDialogComponent {
  private dialog = inject(NgbActiveModal);
  private distributionObjectService = inject(DistributionObjectService);
  private dateTimeService = inject(DateTimeService);

  initialSampleData = model<SampleDocument>(null);
  documentTypeId = model(0);
  xpath = model<string>('');

  sampleIsUploaded = signal(false);
  sampleData = linkedSignal(() => this.initialSampleData());

  dataStore = computed(() => {
    const sampleDocument = this.sampleData();
    const items = this.convertToNodePlainStructure(sampleDocument?.xml);
    return new CustomStore<MyTreeViewNode, number>({
      loadMode: 'raw',
      cacheRawData: false,
      load: () => items
    });
  });

  dataSource = computed(() => new DataSource({ store: this.dataStore() }));

  documentType = resource({
    params: () => this.documentTypeId(),
    loader: ({ params }) => params
        ? this.distributionObjectService.getDistributionObjects().then(a => a.find(e => e.Id === params))
        : Promise.resolve(null as DocumentTypeEntity)
  });

  documentTypeDisplay = computed(() => {
    const documentTypeName = this.documentType.value()?.Name ?? '(missing)';
    const timeStamp = this.sampleData()?.timeStamp;
    if (timeStamp) {
      const documentDate = this.dateTimeService.getDevExpressDateTimeFunction(timeStamp);
      return `${documentTypeName} - ${documentDate}`;
    }
    else {
      return documentTypeName;
    }
  });

  convertToNodePlainStructure(xml: string): MyTreeViewNode[] {
    if (!xml) {
      return [];
    }

    const buildNodePlainStructure: BuildNodePlainStructure = (node, parent, idCounter) => {
      const newNode = {
        id: idCounter.value++,
        parentId: parent?.id ?? null,
        parent,
        expanded: !parent,
        name: node.localName,
        value: node.childElementCount > 0 ? null : node.textContent,
        nodeType: node.nodeType,
        hasItems: false,
        itemCount: 0,
      } as MyTreeViewNode;

      if (parent) {
        parent.hasItems = true; // mark parent as having children
        parent.itemCount++;
      }

      const attrs = Array.from(node.attributes)
        .filter(e => (e.prefix !== 'xmlns') && (e.name !== 'xmlns'))
        .map(e => ({
          id: idCounter.value++,
          parentId: newNode.id,
          parent: newNode,
          expanded: false,
          name: e.localName,
          value: e.value,
          nodeType: NodeType.ATTRIBUTE_NODE,
          hasItems: false,
          itemCount: 0,
        } as MyTreeViewNode));

      const childs = Array.from(node.children)
        .filter(e => e.nodeType === Number(NodeType.ELEMENT_NODE))
        .flatMap(child => buildNodePlainStructure(child, newNode, idCounter));

      return [newNode].concat(attrs, childs);
    }

    const xmlDoc: Document = new DOMParser().parseFromString(xml, 'text/xml');
    const nodes = buildNodePlainStructure(xmlDoc.documentElement, null, { value: 0 });
    return nodes;
  }

  itemClick(e: ItemClickEvent): void {
    const node = e.itemData as MyTreeViewNode;
    this.xpath.set(this.buildXPath(node));
  }

  buildXPath(node: MyTreeViewNode): string {
    let xpath = node.parent ? this.buildXPath(node.parent) : '';
    xpath += node.nodeType === NodeType.ATTRIBUTE_NODE ? `/@*[local-name()='${node.name}']` : `/*[local-name()='${node.name}']`;
    return xpath;
  }

  fileSelected(file: File): void {
    const reader = new FileReader();

    reader.onload = () => {
      this.sampleData.set({
        xml: reader.result as string,
        timeStamp: new Date(file.lastModified),
        fileName: file.name,
        fileSize: file.size,
      });
      this.sampleIsUploaded.set(true);
    };

    this.sampleData.set(null);
    reader.readAsText(file);
  }

  ok(): void {
    this.dialog.close(this.xpath());
  }

  cancel(): void {
    this.dialog.dismiss();
  }
}
